import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtMoney, fmtPct, fmtDate } from "../lib/api";
import { Plus, RefreshCw, Pencil, Trash2, DollarSign, Download, Upload, Library, ArrowRight, FileSpreadsheet, Sparkles, Link2, Hand, FileText, Radio, Search, ArrowUp, ArrowDown, ArrowUpDown, X } from "lucide-react";
import HoldingDialog from "../components/HoldingDialog";
import DividendDialog from "../components/DividendDialog";
import BrokerConnect from "../components/BrokerConnect";
import AllocationDonut from "../components/AllocationDonut";
import { toast } from "sonner";
import { useBilling } from "../context/BillingContext";

// Display labels + colors for the asset-type donut. Kept here (and not in
// chartStyles) because the wording is product-facing — "Babybonds", not
// "babybond" — and lives next to the asset-type sidebar tabs.
const TYPE_LABEL = {
  babybond: "Babybonds", preferred: "Preferred", bdc: "BDCs", reit: "REITs",
  etf: "ETFs", common: "Common", other: "Other", pending: "Pending",
};
const TYPE_COLOR = {
  babybond:  "#9e8193", // paars-dark
  preferred: "#c9b896", // gold
  bdc:       "#88a17e", // groen
  reit:      "#bfaf9d", // stone-warm
  etf:       "#d7b5c9", // paars
  common:    "#dbd6d2", // stone
  other:     "#a86464", // burgundy
  pending:   "#bdb5b1",
};

// Format a timestamp as "4m ago", "2h ago", "3d ago" — for price freshness badges.
function fmtAgo(iso) {
  if (!iso) return null;
  const diffSec = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

// Column metadata: key (for sort), label, accessor (for sort value), align,
// and tooltip help text. Single source of truth — used for both header rendering
// AND sort logic so they can never drift out of sync.
const COLUMNS = [
  { key: "ticker", label: "Ticker", align: "left", accessor: (r) => (r.ticker || "").toUpperCase(),
    help: "Exchange ticker (e.g. ATLCL, ARCC, JPM.PRC, O)" },
  { key: "name", label: "Issuer", align: "left", accessor: (r) => (r.name || "").toLowerCase(),
    help: "Company that issued the security" },
  { key: "asset_type", label: "Type", align: "left", accessor: (r) => (r.asset_type || "pending"),
    help: "Classification: babybond / preferred / bdc / reit / etf / common / other" },
  { key: "par_value", label: "Par", align: "right", accessor: (r) => r.par_value || 0,
    help: "Face value paid back at maturity (typically $25 for babybonds)" },
  { key: "coupon_rate", label: "Coupon", align: "right", accessor: (r) => r.coupon_rate || 0,
    help: "Annual interest rate on par. A 7% coupon on $25 par pays $1.75/yr per bond." },
  { key: "maturity_date", label: "Maturity", align: "left", accessor: (r) => r.maturity_date || "",
    help: "Date the issuer must repay par. The bond is automatically redeemed on this date." },
  { key: "shares", label: "Shares", align: "right", accessor: (r) => r.shares || 0,
    help: "Number of shares/bonds you hold" },
  { key: "cost_basis", label: "Cost", align: "right", accessor: (r) => r.cost_basis || 0,
    help: "Total cost basis (price you paid × shares). Used for P/L and tax." },
  { key: "current_price", label: "Price", align: "right", accessor: (r) => r.current_price || 0,
    help: "Latest market price from Finnhub. Refreshes every 60s in LIVE mode (Pro)." },
  { key: "market_value", label: "Mkt Value", align: "right", accessor: (r) => r.market_value || 0,
    help: "Current market value of your position (price × shares)" },
  { key: "unrealized_pl", label: "P/L", align: "right", accessor: (r) => r.unrealized_pl || 0,
    help: "Unrealized profit/loss. Mkt Value minus Cost. Doesn't count dividends received." },
  { key: "yoc", label: "YOC", align: "right", accessor: (r) => r.yoc || 0,
    help: "Yield on Cost. Annual income ÷ what you paid. Locked in at purchase." },
  { key: "ytm", label: "YTM", align: "right", accessor: (r) => r.ytm == null ? -1 : r.ytm,
    help: "Yield to Maturity. Total annualized return if held to maturity (bonds/preferred only)." },
  { key: "ytc", label: "YTC", align: "right", accessor: (r) => r.ytc == null ? -1 : r.ytc,
    help: "Yield to Call. Annualized return if the issuer redeems at the call_date. Lower than YTM = call risk for premium holders." },
  { key: "annual_income", label: "Income/yr", align: "right", accessor: (r) => r.annual_income || 0,
    help: "Total annual income from this position" },
  { key: "actions", label: "Actions", align: "right", accessor: () => 0, sortable: false, help: "" },
];

// ─── Column visibility per asset_type ────────────────────────────────────
// Single source of truth — each entry is a list of column `key`s.
// Holdings.jsx renders only these columns when `?type=<key>` is active.
// The "all" key keeps the existing kitchen-sink layout for the catch-all view.
const COLUMN_KEYS_BY_TYPE = {
  all:        ["ticker", "name", "asset_type", "shares", "cost_basis", "current_price", "market_value", "unrealized_pl", "yoc", "ytm", "annual_income", "actions"],
  babybond:   ["ticker", "name", "par_value", "coupon_rate", "maturity_date", "shares", "cost_basis", "current_price", "market_value", "unrealized_pl", "yoc", "ytm", "ytc", "annual_income", "actions"],
  preferred:  ["ticker", "name", "par_value", "coupon_rate", "maturity_date", "shares", "cost_basis", "current_price", "market_value", "unrealized_pl", "yoc", "ytm", "ytc", "annual_income", "actions"],
  bdc:        ["ticker", "name", "shares", "cost_basis", "current_price", "market_value", "unrealized_pl", "yoc", "annual_income", "actions"],
  reit:       ["ticker", "name", "shares", "cost_basis", "current_price", "market_value", "unrealized_pl", "yoc", "annual_income", "actions"],
  etf:        ["ticker", "name", "shares", "cost_basis", "current_price", "market_value", "unrealized_pl", "yoc", "annual_income", "actions"],
  common:     ["ticker", "name", "shares", "cost_basis", "current_price", "market_value", "unrealized_pl", "yoc", "annual_income", "actions"],
  other:      ["ticker", "name", "asset_type", "shares", "cost_basis", "current_price", "market_value", "unrealized_pl", "yoc", "annual_income", "actions"],
};

const TYPE_CHIP_CLS = {
  babybond:  "bg-paars/20 text-paars-dark border border-paars/40",
  preferred: "bg-gold/20 text-gold-dark border border-gold/40",
  bdc:       "bg-groen/20 text-groen-dark border border-groen/40",
  reit:      "bg-stone-warm/40 text-graphite/80 border border-stone-warm",
  etf:       "bg-paars-light/30 text-paars-dark border border-paars/30",
  common:    "bg-stone/60 text-graphite/70 border border-stone-warm",
  other:     "bg-burgundy/15 text-burgundy border border-burgundy/30",
  pending:   "bg-champagne text-graphite/45 border border-stone",
};

// Resolve the sort indicator icon for a column header — extracted from inline
// ternary so the JSX header row stays readable.
function getSortIcon(col, sort) {
  if (col.sortable === false) return null;
  if (sort.key !== col.key) return ArrowUpDown;
  return sort.dir === "asc" ? ArrowUp : ArrowDown;
}

// Bucket a price's freshness into a tailwind color class for the "Xm ago" badge.
function priceAgeClass(iso) {
  if (!iso) return "text-graphite/45";
  const ageMin = (Date.now() - new Date(iso).getTime()) / 60000;
  if (ageMin < 5) return "text-groen-dark";
  if (ageMin < 60 * 24) return "text-graphite/45";
  return "text-gold-dark";
}

// LIVE-mode toggle button styling — depends on (liveMode, isPro) combo.
function liveBtnClass(liveMode, isPro) {
  if (liveMode && isPro) return "border border-groen text-groen-dark bg-groen/15 hover:bg-groen/25";
  if (isPro) return "border border-paars-dark/30 text-graphite/80 hover:bg-stone/50";
  return "border border-gold/60 text-gold-dark bg-gold/15 hover:bg-gold/25";
}

export default function Holdings() {
  const { tier } = useBilling();
  const isPro = tier === "pro" || tier === "founder_lifetime";
  const [searchParams, setSearchParams] = useSearchParams();
  // ?type=babybond|preferred|bdc|reit|etf|common|other  → filters server response
  // and picks a column-config subset. Empty/unknown value falls back to "all".
  const rawType = searchParams.get("type");
  const activeType = rawType && COLUMN_KEYS_BY_TYPE[rawType] ? rawType : "all";
  const visibleColumns = useMemo(
    () => COLUMN_KEYS_BY_TYPE[activeType].map((k) => COLUMNS.find((c) => c.key === k)).filter(Boolean),
    [activeType],
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [openDiv, setOpenDiv] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // Free-text filter (searches across ticker + issuer name)
  const [filterText, setFilterText] = useState("");
  // Sort: clicking a header toggles asc → desc → off (no sort)
  const [sort, setSort] = useState({ key: "ticker", dir: "asc" });
  // LIVE mode: default ON for Pro users (auto-refresh prices every 2 min during
  // market hours), persisted in localStorage so the user's explicit OFF choice
  // sticks across sessions. Key is per-tier so demoting Free → Pro doesn't
  // surprise an upgrader with the old "off" preference.
  const [liveMode, setLiveMode] = useState(() => {
    try {
      const saved = localStorage.getItem("bb_live_mode");
      if (saved === "off") return false;
      if (saved === "on") return true;
    } catch (_) { void 0; }
    return true; // default: ON
  });
  const liveTimerRef = useRef(null);
  const initialFetchRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/holdings");
      setRows(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refreshPrices = useCallback(async (silent = false) => {
    setRefreshing(true);
    try {
      const tickers = [...new Set(rows.map((r) => r.ticker))];
      if (tickers.length === 0) return;
      const { data } = await api.post("/quote/batch", { symbols: tickers });
      await load();
      const ok = Object.keys(data.quotes || {}).length;
      const err = Object.keys(data.errors || {}).length;
      if (!silent) {
        if (err > 0) {
          toast.error(`Refreshed ${ok}/${ok + err} · ${err} unavailable: ${Object.keys(data.errors).slice(0, 3).join(", ")}${err > 3 ? "…" : ""}`);
        } else {
          toast.success(`Refreshed ${ok} price${ok === 1 ? "" : "s"}`);
        }
      }
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(false); }
  }, [rows, load]);

  // Persist live-mode preference so user choice survives reloads.
  useEffect(() => {
    try { localStorage.setItem("bb_live_mode", liveMode ? "on" : "off"); } catch (_) { void 0; }
  }, [liveMode]);

  // On first load with holdings: fire one silent batch refresh so the user
  // never lands on a stale-price view. Pro-only (Free has 10-min cache anyway).
  useEffect(() => {
    if (initialFetchRef.current) return;
    if (loading || !isPro || rows.length === 0) return;
    initialFetchRef.current = true;
    refreshPrices(true);
  }, [loading, isPro, rows.length, refreshPrices]);

  // LIVE mode: auto-refresh every 2 min while market is open (Pro only).
  // We piggyback on the backend's market-status endpoint so we don't burn API quota
  // when the market is closed (overnight/weekend).
  useEffect(() => {
    if (!liveMode || !isPro) {
      if (liveTimerRef.current) clearInterval(liveTimerRef.current);
      return;
    }
    const tick = async () => {
      try {
        const { data: ms } = await api.get("/quote/market-status");
        if (ms.is_open) await refreshPrices(true);
      } catch (e) { void e; }
    };
    tick(); // immediate first run
    liveTimerRef.current = setInterval(tick, 120_000); // 2 min
    return () => { if (liveTimerRef.current) clearInterval(liveTimerRef.current); };
  }, [liveMode, isPro, refreshPrices]);

  // ───────── Filter + sort (client-side) ─────────
  const displayRows = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    let out = rows;
    if (q) {
      out = rows.filter((r) =>
        (r.ticker || "").toLowerCase().includes(q) ||
        (r.name || "").toLowerCase().includes(q)
      );
    }
    if (sort.key) {
      const col = COLUMNS.find((c) => c.key === sort.key);
      if (col && col.sortable !== false) {
        const acc = col.accessor;
        const dir = sort.dir === "desc" ? -1 : 1;
        out = [...out].sort((a, b) => {
          const va = acc(a), vb = acc(b);
          if (va < vb) return -1 * dir;
          if (va > vb) return 1 * dir;
          return 0;
        });
      }
    }
    return out;
  }, [rows, filterText, sort]);

  // ───────── Portfolio summary (computed across FILTERED rows) ─────────
  const summary = useMemo(() => {
    const totals = displayRows.reduce(
      (acc, r) => ({
        positions: acc.positions + 1,
        cost: acc.cost + (r.cost_basis || 0),
        market: acc.market + (r.market_value || 0),
        pl: acc.pl + (r.unrealized_pl || 0),
        income: acc.income + (r.annual_income || 0),
      }),
      { positions: 0, cost: 0, market: 0, pl: 0, income: 0 }
    );
    const pl_pct = totals.cost > 0 ? (totals.pl / totals.cost) * 100 : 0;
    const portfolio_yield = totals.cost > 0 ? (totals.income / totals.cost) * 100 : 0;
    return { ...totals, pl_pct, portfolio_yield };
  }, [displayRows]);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: "asc" };
      if (prev.dir === "asc") return { key, dir: "desc" };
      return { key: null, dir: null }; // third click → no sort
    });
  };

  const onDelete = async (id) => {
    if (!confirm("Delete this position and all its dividend records?")) return;
    await api.delete(`/holdings/${id}`);
    toast.success("Holding deleted");
    load();
  };

  const downloadCSV = async (url, filename) => {
    const res = await api.get(url, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/holdings/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data.errors?.length) {
        const rows = data.errors.slice(0, 5).map(e => e.row || "?").join(", ");
        const more = data.errors.length > 5 ? "…" : "";
        toast.error(`Imported ${data.created} rows · ${data.errors.length} errors (rows: ${rows}${more})`);
      } else {
        toast.success(`Imported ${data.created} positions`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="min-h-screen bg-champagne">
      <div className="border-b border-stone px-8 py-5 flex items-center justify-between sticky top-0 bg-champagne z-10">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold">// SYS::HOLDINGS{activeType !== "all" ? `::${activeType.toUpperCase()}` : ""}</div>
          <h1 className="text-2xl font-bold tracking-tight text-graphite flex items-center gap-3" data-testid="holdings-page-title">
            {activeType === "all" ? "All Positions" : `${TYPE_LABEL[activeType] || activeType}`}
            {activeType !== "all" && (
              <button
                data-testid="clear-type-filter"
                onClick={() => setSearchParams({})}
                title="Show all asset types"
                className="text-[10px] tracking-[0.2em] uppercase font-mono text-graphite/55 hover:text-paars-dark border border-stone px-2 py-1 flex items-center gap-1"
              >
                <X className="w-3 h-3" strokeWidth={2} /> Clear filter
              </button>
            )}
          </h1>
        </div>
        <div className="flex gap-2">
          <input id="csv-input" type="file" accept=".csv" onChange={onImport} className="hidden" data-testid="csv-file-input" />
          <button data-testid="template-btn" onClick={()=>downloadCSV("/holdings/template", "babybonds_template.csv")} className="px-3 py-2.5 border border-paars-dark/30 text-graphite/80 text-xs hover:bg-stone/50 flex items-center gap-1.5">
            <Download className="w-3.5 h-3.5" /> TEMPLATE
          </button>
          <button data-testid="import-btn" onClick={()=>document.getElementById("csv-input").click()} className="px-3 py-2.5 border border-paars-dark/30 text-graphite/80 text-xs hover:bg-stone/50 flex items-center gap-1.5">
            <Upload className="w-3.5 h-3.5" /> IMPORT
          </button>
          <button data-testid="export-btn" onClick={()=>downloadCSV("/holdings/export", `babybonds_holdings_${new Date().toISOString().slice(0,10)}.csv`)} disabled={rows.length===0} className="px-3 py-2.5 border border-paars-dark/30 text-graphite/80 text-xs hover:bg-stone/50 flex items-center gap-1.5 disabled:opacity-50">
            <Download className="w-3.5 h-3.5" /> EXPORT
          </button>
          <button data-testid="refresh-prices-btn" onClick={() => refreshPrices(false)} disabled={refreshing || rows.length===0} className="px-3 py-2.5 border border-paars-dark/30 text-graphite/80 text-xs hover:bg-stone/50 flex items-center gap-1.5 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} /> REFRESH
          </button>
          <button
            data-testid="live-toggle-btn"
            onClick={() => isPro ? setLiveMode(v => !v) : toast.message("LIVE auto-refresh is a Pro feature")}
            disabled={rows.length === 0}
            title={isPro ? (liveMode ? "Stop auto-refresh" : "Auto-refresh every 2 min during market hours") : "Pro feature"}
            className={`px-3 py-2.5 text-xs flex items-center gap-1.5 disabled:opacity-50 ${liveBtnClass(liveMode, isPro)}`}
          >
            <Radio className={`w-3.5 h-3.5 ${liveMode && isPro ? "animate-pulse" : ""}`} />
            {liveMode && isPro ? "LIVE · ON" : isPro ? "LIVE" : "LIVE (PRO)"}
          </button>
          <BrokerConnect variant="button" onSynced={load} />
          <Link to="/catalog" data-testid="open-catalog-btn" className="px-4 py-2.5 border border-paars text-paars-dark text-xs font-semibold hover:bg-paars/20 flex items-center gap-1.5">
            <Library className="w-3.5 h-3.5" /> FROM CATALOG
          </Link>
          <button data-testid="add-position-btn" onClick={()=>{setEditing(null); setOpenDialog(true);}} className="px-4 py-2.5 bg-paars text-graphite font-semibold text-xs hover:bg-paars-light flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> ADD MANUAL
          </button>
        </div>
      </div>

      <div className="p-8">
        {!loading && rows.length === 0 ? (
          <EmptyHoldingsState
            onAddManual={() => { setEditing(null); setOpenDialog(true); }}
            onImportClick={() => document.getElementById("csv-input").click()}
            onTemplateClick={() => downloadCSV("/holdings/template", "babybonds_template.csv")}
          />
        ) : (
        <>
        {/* ───────── Allocation donuts — by Asset Type & Sector ─────────
           Operate on displayRows so they live-update when the user clicks
           an asset-type tab (e.g. clicking "BDCs" shows sector breakdown
           of just the BDCs portion of the portfolio). */}
        {displayRows.length > 0 && (
          <div data-testid="holdings-allocation-row" className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <AllocationDonut
              title="By Asset Type"
              rows={displayRows}
              groupKey="asset_type"
              labelMap={TYPE_LABEL}
              colorMap={TYPE_COLOR}
              emptyLabel="Pending"
              testId="alloc-type"
            />
            <AllocationDonut
              title="By Sector / Industry"
              rows={displayRows.map((r) => ({ ...r, sector_industry: r.sector || r.industry || null }))}
              groupKey="sector_industry"
              emptyLabel="Unclassified"
              testId="alloc-sector"
            />
          </div>
        )}

        {/* Filter bar — search across ticker + issuer name */}
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-graphite/45" />
            <input
              data-testid="holdings-search-input"
              type="text"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder="Filter by ticker or issuer…"
              className="w-full pl-9 pr-9 py-2 text-sm bg-champagne border border-stone focus:outline-none focus:border-paars text-graphite"
            />
            {filterText && (
              <button
                onClick={() => setFilterText("")}
                title="Clear filter"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-graphite/45 hover:text-graphite text-xs"
              >
                ×
              </button>
            )}
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-mono text-graphite/55">
            Showing {displayRows.length} of {rows.length}{activeType !== "all" ? ` · ${TYPE_LABEL[activeType] || activeType}` : ""}{sort.key ? ` · sorted by ${sort.key} ${sort.dir}` : ""}
          </div>
        </div>

        <div className="border border-stone bg-champagne overflow-x-auto max-h-[calc(100vh-280px)] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="bg-stone/95 backdrop-blur-sm border-b border-stone shadow-sm">
                {visibleColumns.map((col) => {
                  const isSorted = sort.key === col.key;
                  const SortIcon = getSortIcon(col, sort);
                  return (
                    <th
                      key={col.key}
                      data-testid={`col-header-${col.key}`}
                      title={col.help}
                      onClick={col.sortable === false ? undefined : () => toggleSort(col.key)}
                      className={`px-3 py-3 text-[10px] tracking-[0.2em] uppercase font-bold ${
                        col.align === "right" ? "text-right" : "text-left"
                      } ${isSorted ? "text-paars-dark" : "text-graphite/60"} ${
                        col.sortable === false ? "" : "cursor-pointer hover:text-paars-dark select-none"
                      }`}
                    >
                      <span className={`inline-flex items-center gap-1 ${col.align === "right" ? "justify-end" : ""}`}>
                        {col.label}
                        {SortIcon && col.sortable !== false && (
                          <SortIcon className={`w-3 h-3 ${isSorted ? "opacity-100" : "opacity-30"}`} strokeWidth={2.5} />
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={visibleColumns.length} className="p-6 text-center text-graphite/45 font-mono text-xs">LOADING...</td></tr>
              )}
              {!loading && displayRows.length === 0 && (
                <tr><td colSpan={visibleColumns.length} className="p-6 text-center text-graphite/55 font-mono text-xs">
                  {activeType !== "all" && rows.length > 0
                    ? <>No <span className="text-paars-dark font-semibold">{TYPE_LABEL[activeType]}</span> positions yet. <button onClick={() => setSearchParams({})} className="underline hover:text-paars-dark">View all</button></>
                    : <>No holdings match &ldquo;{filterText}&rdquo;</>}
                </td></tr>
              )}
              {displayRows.map((r) => (
                <HoldingRow
                  key={r.id}
                  row={r}
                  columns={visibleColumns}
                  onRecordDiv={() => { setPrefill(r.id); setOpenDiv(true); }}
                  onEdit={() => { setEditing(r); setOpenDialog(true); }}
                  onDelete={() => onDelete(r.id)}
                />
              ))}
            </tbody>
          </table>
        </div>

        {/* ───────── Portfolio summary ───────── */}
        <div data-testid="holdings-summary" className="mt-6 bg-champagne border border-stone">
          <div className="px-5 py-3 border-b border-stone flex items-center justify-between">
            <div className="text-[10px] tracking-[0.3em] uppercase text-graphite/55 font-bold">
              // PORTFOLIO_SUMMARY
            </div>
            {filterText && (
              <div className="text-[10px] tracking-[0.15em] uppercase text-paars-dark font-semibold">
                Filtered view
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 divide-x divide-stone">
            <SummaryCell
              testId="summary-positions"
              label="Positions"
              value={summary.positions.toLocaleString()}
              sub={`${rows.length} total holding${rows.length === 1 ? "" : "s"}`}
            />
            <SummaryCell
              testId="summary-cost"
              label="Total cost"
              value={fmtMoney(summary.cost)}
              sub="What you paid in"
            />
            <SummaryCell
              testId="summary-market-value"
              label="Total market value"
              value={fmtMoney(summary.market)}
              sub={summary.market - summary.cost >= 0 ? "Currently worth" : "Currently worth"}
            />
            <SummaryCell
              testId="summary-pl"
              label="Unrealized P/L"
              value={(summary.pl >= 0 ? "+" : "") + fmtMoney(summary.pl)}
              sub={(summary.pl_pct >= 0 ? "+" : "") + fmtPct(summary.pl_pct)}
              accent={summary.pl >= 0 ? "text-groen-dark" : "text-burgundy"}
            />
            <SummaryCell
              testId="summary-income"
              label="Income / year"
              value={fmtMoney(summary.income)}
              sub={`Portfolio yield ${fmtPct(summary.portfolio_yield)}`}
              accent="text-paars-dark"
            />
          </div>
        </div>
        </>
        )}
      </div>

      <HoldingDialog open={openDialog} onOpenChange={setOpenDialog} editing={editing} onSaved={load} />
      <DividendDialog open={openDiv} onOpenChange={setOpenDiv} holdings={rows} prefillHoldingId={prefill} onSaved={load} />
    </div>
  );
}

function SummaryCell({ testId, label, value, sub, accent = "text-graphite" }) {
  return (
    <div data-testid={testId} className="px-5 py-4">
      <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold mb-1">{label}</div>
      <div className={`text-xl font-mono font-semibold ${accent}`}>{value}</div>
      {sub && <div className="text-[11px] text-graphite/55 mt-1">{sub}</div>}
    </div>
  );
}

// Render ONLY the columns visible for the current asset_type view.
// Each cell is keyed off the column.key — the actual cell content is a small
// switch statement so adding a new column means: (1) add to COLUMNS, (2) add a
// case here, (3) reference it in COLUMN_KEYS_BY_TYPE for whichever types use it.
function HoldingRow({ row: r, columns, onRecordDiv, onEdit, onDelete }) {
  const plPos = (r.unrealized_pl || 0) >= 0;
  return (
    <tr data-testid={`holding-row-${r.ticker}`} className="border-b border-stone/60 hover:bg-white transition-colors">
      {columns.map((col) => {
        if (col.key === "ticker") return (
          <td key="ticker" className="px-3 py-3">
            <div className="flex items-center gap-2">
              <SourceBadge source={r.source} />
              <div className="font-mono font-semibold text-paars-dark">{r.ticker}</div>
            </div>
          </td>
        );
        if (col.key === "name") return (
          <td key="name" className="px-3 py-3 text-graphite/60 max-w-[180px] truncate">{r.name || "—"}</td>
        );
        if (col.key === "asset_type") {
          const t = r.asset_type || "pending";
          return (
            <td key="asset_type" className="px-3 py-3">
              <span
                title={`Classified as ${TYPE_LABEL[t] || t}`}
                data-testid={`type-chip-${r.ticker}`}
                className={`inline-block px-2 py-0.5 text-[10px] font-mono tracking-widest uppercase ${TYPE_CHIP_CLS[t] || TYPE_CHIP_CLS.pending}`}
              >
                {TYPE_LABEL[t] || t}
              </span>
            </td>
          );
        }
        if (col.key === "par_value") return (
          <td key="par_value" className="px-3 py-3 font-mono text-right text-graphite/80">{fmtMoney(r.par_value)}</td>
        );
        if (col.key === "coupon_rate") return (
          <td key="coupon_rate" className="px-3 py-3 font-mono text-right text-graphite/80">{r.coupon_rate ? fmtPct(r.coupon_rate) : "—"}</td>
        );
        if (col.key === "maturity_date") return (
          <td key="maturity_date" className="px-3 py-3 font-mono text-graphite/60">{r.maturity_date ? fmtDate(r.maturity_date) : "—"}</td>
        );
        if (col.key === "shares") return (
          <td key="shares" className="px-3 py-3 font-mono text-right text-graphite/80">{r.shares}</td>
        );
        if (col.key === "cost_basis") return (
          <td key="cost_basis" className="px-3 py-3 font-mono text-right text-graphite/80">{fmtMoney(r.cost_basis)}</td>
        );
        if (col.key === "current_price") return (
          <td key="current_price" className="px-3 py-3 font-mono text-right text-graphite">
            {r.current_price ? (
              <>
                <div>{fmtMoney(r.current_price)}</div>
                {r.last_price_update && (
                  <div
                    className={`text-[10px] ${priceAgeClass(r.last_price_update)}`}
                    title={`Last update: ${new Date(r.last_price_update).toLocaleString()}`}
                  >
                    {fmtAgo(r.last_price_update)}
                  </div>
                )}
              </>
            ) : (
              <span className="text-graphite/45">—</span>
            )}
          </td>
        );
        if (col.key === "market_value") return (
          <td key="market_value" className="px-3 py-3 font-mono text-right text-graphite">{fmtMoney(r.market_value)}</td>
        );
        if (col.key === "unrealized_pl") return (
          <td key="unrealized_pl" className={`px-3 py-3 font-mono text-right ${plPos ? "text-groen-dark" : "text-burgundy"}`}>
            {plPos ? "+" : ""}{fmtMoney(r.unrealized_pl)}
            <div className="text-[10px] text-graphite/45">{plPos?"+":""}{fmtPct(r.unrealized_pl_pct)}</div>
          </td>
        );
        if (col.key === "yoc") return (
          <td key="yoc" className="px-3 py-3 font-mono text-right text-paars-dark">{r.yoc ? fmtPct(r.yoc) : "—"}</td>
        );
        if (col.key === "ytm") return (
          <td key="ytm" className="px-3 py-3 font-mono text-right text-paars-dark">{r.ytm !== null && r.ytm !== undefined ? fmtPct(r.ytm) : "—"}</td>
        );
        if (col.key === "ytc") return (
          <td key="ytc" className={`px-3 py-3 font-mono text-right ${r.ytc != null && r.ytc < 0 ? "text-burgundy" : "text-gold-dark"}`} title="Yield to Call">{r.ytc !== null && r.ytc !== undefined ? fmtPct(r.ytc) : "—"}</td>
        );
        if (col.key === "annual_income") return (
          <td key="annual_income" className="px-3 py-3 font-mono text-right text-groen-dark">{fmtMoney(r.annual_income)}</td>
        );
        if (col.key === "actions") return (
          <td key="actions" className="px-3 py-3">
            <div className="flex items-center justify-end gap-1">
              <button data-testid={`record-div-${r.ticker}`} onClick={onRecordDiv} title="Record dividend" className="p-1.5 text-groen-dark hover:bg-stone/50">
                <DollarSign className="w-3.5 h-3.5" />
              </button>
              <button data-testid={`edit-${r.ticker}`} onClick={onEdit} title="Edit" className="p-1.5 text-graphite/60 hover:text-graphite hover:bg-stone/50">
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button data-testid={`delete-${r.ticker}`} onClick={onDelete} title="Delete" className="p-1.5 text-graphite/60 hover:text-burgundy hover:bg-stone/50">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </td>
        );
        return <td key={col.key} className="px-3 py-3" />;
      })}
    </tr>
  );
}

// Maps a source string to the badge config (Icon, label, css class).
const SOURCE_BADGE = {
  snaptrade:  { Icon: Link2,    label: "Broker-synced (read-only)", cls: "bg-paars/25 text-paars-dark border border-paars/40" },
  csv_import: { Icon: FileText, label: "CSV import",                cls: "bg-stone/60 text-graphite/70 border border-stone-warm" },
  manual:     { Icon: Hand,     label: "Manual entry",              cls: "bg-champagne text-graphite/55 border border-stone" },
};

function SourceBadge({ source }) {
  const { Icon, label, cls } = SOURCE_BADGE[source] || SOURCE_BADGE.manual;
  return (
    <span
      title={label}
      data-testid={`source-badge-${source || "manual"}`}
      className={`inline-flex items-center justify-center w-6 h-6 ${cls}`}
    >
      <Icon className="w-3 h-3" strokeWidth={2.5} />
    </span>
  );
}

function EmptyHoldingsState({ onAddManual, onImportClick, onTemplateClick }) {
  return (
    <div data-testid="holdings-empty-state" className="border border-stone bg-champagne">      {/* Hero */}
      <div className="relative overflow-hidden border-b border-stone bg-graphite px-10 py-14">
        <div className="absolute inset-0 terminal-grid opacity-20" />
        <div className="absolute -right-20 -top-20 w-64 h-64 bg-paars/15 blur-3xl rounded-full" />
        <div className="relative z-10 max-w-2xl">
          <div className="text-[10px] tracking-[0.35em] uppercase text-paars mb-3 font-bold">// PORTFOLIO_EMPTY</div>
          <h2 className="text-4xl font-bold text-champagne tracking-tight leading-tight">
            Your portfolio is a blank slate.
            <br />
            <span className="text-paars glow-paars">Let&apos;s add your first babybond.</span>
          </h2>
          <p className="mt-5 text-sm text-stone/70 leading-relaxed max-w-xl">
            Pick one of the three paths below. Yield on Cost, Yield to Maturity and projected
            payments will calculate automatically once you have a position on file.
          </p>
        </div>
      </div>

      {/* Three paths */}
      <div className="grid md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-stone">
        {/* Path 1 — Catalog (recommended) */}
        <Link
          to="/catalog"
          data-testid="empty-path-catalog"
          className="group relative p-8 hover:bg-stone/30 transition-colors"
        >
          <div className="absolute top-3 right-3 text-[9px] tracking-[0.25em] uppercase text-groen-dark font-bold bg-groen/15 px-2 py-1">
            Recommended
          </div>
          <div className="w-10 h-10 bg-paars flex items-center justify-center mb-5">
            <Library className="w-5 h-5 text-graphite" strokeWidth={2.2} />
          </div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold mb-2">// 01 · CATALOG</div>
          <h3 className="text-lg font-bold text-graphite mb-2 tracking-tight">Pick from the master catalog</h3>
          <p className="text-xs text-graphite/65 leading-relaxed mb-6">
            Browse the curated list of babybonds. Ticker, coupon and maturity auto-fill — you only
            enter shares and cost basis.
          </p>
          <div className="flex items-center gap-1.5 text-xs font-semibold text-paars-dark group-hover:translate-x-1 transition-transform">
            BROWSE CATALOG <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </Link>

        {/* Path 2 — CSV Import */}
        <div className="p-8 flex flex-col">
          <div className="w-10 h-10 bg-stone-warm flex items-center justify-center mb-5">
            <FileSpreadsheet className="w-5 h-5 text-graphite" strokeWidth={2.2} />
          </div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold mb-2">// 02 · BULK IMPORT</div>
          <h3 className="text-lg font-bold text-graphite mb-2 tracking-tight">Import a CSV</h3>
          <p className="text-xs text-graphite/65 leading-relaxed mb-6">
            Already tracking in a spreadsheet? Download the template, fill it in, and import all
            positions at once.
          </p>
          <div className="flex flex-col gap-2 mt-auto">
            <button
              data-testid="empty-path-import"
              onClick={onImportClick}
              className="w-full px-4 py-2.5 bg-graphite text-champagne text-xs font-semibold tracking-wider uppercase hover:bg-graphite/90 flex items-center justify-center gap-2"
            >
              <Upload className="w-3.5 h-3.5" /> Upload CSV
            </button>
            <button
              data-testid="empty-path-template"
              onClick={onTemplateClick}
              className="w-full px-4 py-2 border border-stone-warm text-graphite/70 text-[11px] tracking-wider uppercase hover:bg-stone/40 flex items-center justify-center gap-2"
            >
              <Download className="w-3 h-3" /> Download template
            </button>
          </div>
        </div>

        {/* Path 3 — Manual */}
        <div className="p-8 flex flex-col">
          <div className="w-10 h-10 bg-paars-dark flex items-center justify-center mb-5">
            <Sparkles className="w-5 h-5 text-champagne" strokeWidth={2.2} />
          </div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold mb-2">// 03 · MANUAL</div>
          <h3 className="text-lg font-bold text-graphite mb-2 tracking-tight">Add a position manually</h3>
          <p className="text-xs text-graphite/65 leading-relaxed mb-6">
            Bond not in the catalog yet? Enter ticker, par, coupon, maturity, shares and cost basis
            yourself. Takes about a minute.
          </p>
          <button
            data-testid="empty-path-manual"
            onClick={onAddManual}
            className="mt-auto w-full px-4 py-2.5 bg-paars text-graphite text-xs font-semibold tracking-wider uppercase hover:bg-paars-light flex items-center justify-center gap-2"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Add manually
          </button>
        </div>
      </div>

      {/* Footer hint */}
      <div className="border-t border-stone bg-stone/30 px-8 py-4 flex items-center justify-between">
        <div className="text-[10px] font-mono tracking-widest text-graphite/55">
          NEXT: SHARES + COST BASIS → YOC, YTM &amp; FORECAST AUTO-COMPUTE
        </div>
        <div className="text-[10px] font-mono tracking-widest text-graphite/45">
          SESSION_LIFETIME=7D
        </div>
      </div>
    </div>
  );
}
